import { useState, useMemo, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useApi } from '../../hooks/useApi'
import { apiPost, apiPut } from '../../lib/auth'
import { AlertTriangle, Info, ChevronDown, ChevronRight, ArrowLeft } from 'lucide-react'
import { LlmLogo } from '../../components/icons/LlmLogos'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuditCost {
  provider: string
  model: string
  tokensIn: number
  tokensOut: number
  cost: string
}

interface AuditData {
  id: number
  campaignId: number
  status: string
  startedAt: string | null
  completedAt: string | null
  createdAt: string
  analysis?: string | null
  campaign: { id: number; name: string; brand: string; brandVariants?: string[]; competitors?: string[] }
  costs: AuditCost[]
  result: {
    scores: { overall: number; global?: number; chatgpt: number; gemini: number; claude: number }
    by_category: Record<string, any>
    competitors: Record<string, number>
    raw_results: RawResult[]
    recommendations: Recommendation[]
    tokens_total: Record<string, { input: number; output: number }>
  }
}

interface RawResult {
  query: string
  category: string
  responses: Record<
    'chatgpt' | 'gemini' | 'claude',
    {
      text: string
      cited: boolean
      model: string
      sources: { url: string; title: string }[]
      latencyMs: number
      citation_rate: number
      competitors_cited: string[]
      error?: string
    }
  >
}

interface Recommendation {
  type: 'warning' | 'info'
  title: string
  description: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function scoreColor(score: number): string {
  if (score >= 60) return '#22C55E'
  if (score >= 40) return '#F59E0B'
  return '#EF4444'
}

function scoreBg(score: number): string {
  if (score >= 60) return 'bg-green-100 text-green-700'
  if (score >= 40) return 'bg-yellow-100 text-yellow-700'
  return 'bg-red-100 text-red-700'
}

function consistencyBadge(c: string) {
  const map: Record<string, { bg: string; label: string }> = {
    absent:   { bg: 'bg-red-100 text-red-700', label: 'Absent' },
    fragile:  { bg: 'bg-orange-100 text-orange-700', label: 'Fragile' },
    moderate: { bg: 'bg-yellow-100 text-yellow-700', label: 'Modéré' },
    solid:    { bg: 'bg-green-100 text-green-700', label: 'Solide' },
  }
  const s = map[c] ?? { bg: 'bg-gray-100 text-gray-600', label: c }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.bg}`}>
      {s.label}
    </span>
  )
}

function categoryBadge(cat: string) {
  const colors: Record<string, string> = {
    A: 'bg-[#6366F1]/10 text-[#6366F1]',
    B: 'bg-blue-100 text-blue-700',
    C: 'bg-purple-100 text-purple-700',
  }
  return (
    <span
      className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${colors[cat] ?? 'bg-gray-100 text-gray-600'}`}
    >
      {cat}
    </span>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function fmtNum(n: number): string {
  return n.toLocaleString('fr-FR')
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const LLM_TABS = ['chatgpt', 'gemini', 'claude'] as const
type LLMKey = (typeof LLM_TABS)[number]

const LLM_LABELS: Record<LLMKey, string> = {
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  claude: 'Claude',
}

// Highlight brand + competitors in markdown text
function highlightBrands(text: string, brand: string, brandVariants: string[], competitors: string[]): string {
  if (!text) return ''
  let result = text
  // Build list of all terms to highlight
  const brandTerms = [brand, ...brandVariants].filter(Boolean)
  const compTerms = competitors.filter(Boolean)

  // Wrap brand mentions with a marker (using zero-width markers to avoid breaking markdown)
  for (const term of brandTerms) {
    const re = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    result = result.replace(re, '==BRAND_START==$1==BRAND_END==')
  }
  for (const term of compTerms) {
    const re = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    result = result.replace(re, '==COMP_START==$1==COMP_END==')
  }
  return result
}

// Custom markdown components for LLM responses
const mdComponents = {
  p: ({ children }: any) => <p className="mb-2 last:mb-0">{children}</p>,
  h1: ({ children }: any) => <h3 className="text-base font-bold text-[#1B2559] mt-3 mb-1">{children}</h3>,
  h2: ({ children }: any) => <h3 className="text-sm font-bold text-[#1B2559] mt-3 mb-1">{children}</h3>,
  h3: ({ children }: any) => <h4 className="text-sm font-semibold text-[#1B2559] mt-2 mb-1">{children}</h4>,
  ul: ({ children }: any) => <ul className="list-disc list-inside mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }: any) => <ol className="list-decimal list-inside mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }: any) => <li className="text-sm">{children}</li>,
  a: ({ href, children }: any) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#6366F1] hover:underline">{children}</a>
  ),
  strong: ({ children }: any) => <strong className="font-semibold text-[#1B2559]">{children}</strong>,
  hr: () => <hr className="my-3 border-[#E2E4ED]" />,
  blockquote: ({ children }: any) => <blockquote className="border-l-2 border-[#6366F1]/30 pl-3 my-2 text-[#555A73] italic">{children}</blockquote>,
  table: ({ children }: any) => <table className="w-full text-xs border border-[#E2E4ED] my-2">{children}</table>,
  th: ({ children }: any) => <th className="border border-[#E2E4ED] px-2 py-1 bg-[#F4F5FA] text-left text-[#1B2559] font-medium">{children}</th>,
  td: ({ children }: any) => <td className="border border-[#E2E4ED] px-2 py-1">{children}</td>,
}

// Render markdown with highlighted brands
function LlmResponseContent({ text, brand, brandVariants, competitors }: {
  text: string
  brand: string
  brandVariants: string[]
  competitors: string[]
}) {
  // Build components that highlight brand/competitor mentions in text nodes
  const components = useMemo(() => {
    const allTerms = [
      ...[brand, ...brandVariants].filter(Boolean).map(t => ({ term: t, type: 'brand' as const })),
      ...competitors.filter(Boolean).map(t => ({ term: t, type: 'comp' as const })),
    ].sort((a, b) => b.term.length - a.term.length)

    const highlightText = (text: string) => {
      if (!allTerms.length || typeof text !== 'string') return text
      const pattern = allTerms.map(t => t.term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')
      const regex = new RegExp(`(${pattern})`, 'gi')
      const parts = text.split(regex)
      if (parts.length === 1) return text
      return parts.map((part, i) => {
        const match = allTerms.find(t => t.term.toLowerCase() === part.toLowerCase())
        if (match?.type === 'brand') return <mark key={i} className="bg-emerald-100 text-emerald-800 px-0.5 rounded font-semibold">{part}</mark>
        if (match?.type === 'comp') return <mark key={i} className="bg-amber-100 text-amber-800 px-0.5 rounded">{part}</mark>
        return <span key={i}>{part}</span>
      })
    }

    return {
      ...mdComponents,
      // Intercept text in paragraphs/list items to highlight
      p: ({ children }: any) => <p className="mb-2 last:mb-0">{typeof children === 'string' ? highlightText(children) : children}</p>,
      li: ({ children }: any) => <li className="text-sm">{typeof children === 'string' ? highlightText(children) : children}</li>,
      strong: ({ children }: any) => <strong className="font-semibold text-[#1B2559]">{typeof children === 'string' ? highlightText(children) : children}</strong>,
      td: ({ children }: any) => <td className="border border-[#E2E4ED] px-2 py-1">{typeof children === 'string' ? highlightText(children) : children}</td>,
    }
  }, [brand, brandVariants, competitors])

  return (
    <div className="prose-sm text-[#555A73] leading-relaxed text-[13px]">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  )
}

// Highlight brand/competitor names with colored backgrounds in plain text
function HighlightedText({ text, brand, brandVariants, competitors }: {
  text: string
  brand: string
  brandVariants: string[]
  competitors: string[]
}) {
  const allTerms = useMemo(() => {
    const brandTerms = [brand, ...brandVariants].filter(Boolean).map(t => ({ term: t, type: 'brand' as const }))
    const compTerms = competitors.filter(Boolean).map(t => ({ term: t, type: 'comp' as const }))
    return [...brandTerms, ...compTerms].sort((a, b) => b.term.length - a.term.length) // longest first
  }, [brand, brandVariants, competitors])

  if (!allTerms.length) return <>{text}</>

  const pattern = allTerms.map(t => t.term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')
  const regex = new RegExp(`(${pattern})`, 'gi')
  const parts = text.split(regex)

  return (
    <>
      {parts.map((part, i) => {
        const match = allTerms.find(t => t.term.toLowerCase() === part.toLowerCase())
        if (match?.type === 'brand') {
          return <mark key={i} className="bg-emerald-100 text-emerald-800 px-0.5 rounded font-medium">{part}</mark>
        }
        if (match?.type === 'comp') {
          return <mark key={i} className="bg-amber-100 text-amber-800 px-0.5 rounded">{part}</mark>
        }
        return <span key={i}>{part}</span>
      })}
    </>
  )
}

function BreakdownTable({ results, llm, brand, brandVariants, competitors }: { results: RawResult[]; llm: LLMKey; brand: string; brandVariants: string[]; competitors: string[] }) {
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null)

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#E2E4ED] text-left text-xs text-[#555A73] uppercase tracking-wider">
            <th className="px-4 py-3 w-8" />
            <th className="px-4 py-3">Requ&ecirc;te</th>
            <th className="px-4 py-3">Cat.</th>
            <th className="px-4 py-3">Citation</th>
            <th className="px-4 py-3">Taux</th>
          </tr>
        </thead>
        <tbody>
          {(results ?? []).map((r, idx) => {
            const resp = r.responses?.[llm]
            if (!resp) return null
            const isOpen = expandedIdx === idx

            return (
              <>
                <tr
                  key={`row-${idx}`}
                  onClick={() => setExpandedIdx(isOpen ? null : idx)}
                  className="border-b border-[#E2E4ED] last:border-0 hover:bg-[#F8F9FC] cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-[#555A73]">
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </td>
                  <td className="px-4 py-3 text-[#1B2559] max-w-xs truncate">{r.query}</td>
                  <td className="px-4 py-3">{categoryBadge(r.category)}</td>
                  <td className="px-4 py-3">
                    {resp.cited ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Oui
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        Non
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#1B2559] font-medium">
                    {Math.round(resp.citation_rate)}%
                  </td>
                </tr>

                {isOpen && (
                  <tr key={`detail-${idx}`} className="bg-[#F8F9FC]">
                    <td colSpan={5} className="px-6 py-4">
                      <div className="rounded-lg border border-[#E2E4ED] bg-white p-4 space-y-3">
                        {/* Header: model, latency, cited badges */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-[#555A73]">{resp.model}</span>
                            <span className="text-xs text-[#8B8FA7]">{resp.latencyMs}ms</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {resp.cited ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">
                                <mark className="bg-emerald-300 w-1.5 h-1.5 rounded-full inline-block" />
                                Marque cit&eacute;e
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-600">
                                Marque absente
                              </span>
                            )}
                            {resp.competitors_cited?.length > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700">
                                {resp.competitors_cited.length} concurrent{resp.competitors_cited.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>

                        {resp.error && (
                          <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                            {resp.error}
                          </div>
                        )}

                        {/* LLM Response as rendered markdown with highlighted brands */}
                        {resp.text && (
                          <div className="max-h-[500px] overflow-y-auto border border-[#E2E4ED] rounded-lg p-4 bg-[#FAFBFE]">
                            <LlmResponseContent
                              text={resp.text}
                              brand={brand}
                              brandVariants={brandVariants}
                              competitors={competitors}
                            />
                          </div>
                        )}

                        {/* Legend */}
                        <div className="flex items-center gap-4 text-[10px] text-[#8B8FA7]">
                          <span className="flex items-center gap-1">
                            <mark className="bg-emerald-100 text-emerald-800 px-1 rounded text-[10px]">marque</mark>
                            = votre marque
                          </span>
                          <span className="flex items-center gap-1">
                            <mark className="bg-amber-100 text-amber-800 px-1 rounded text-[10px]">concurrent</mark>
                            = concurrent
                          </span>
                        </div>

                        {/* Sources */}
                        {resp.sources?.length > 0 && (
                          <div className="pt-2 border-t border-[#E2E4ED]">
                            <p className="text-xs font-medium text-[#1B2559] mb-1.5">Sources cit&eacute;es</p>
                            <div className="flex flex-wrap gap-1.5">
                              {resp.sources.map((s, si) => (
                                <a
                                  key={si}
                                  href={s.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] bg-[#F4F5FA] text-[#555A73] hover:bg-[#6366F1]/10 hover:text-[#6366F1] transition-colors truncate max-w-xs"
                                >
                                  {s.title || new URL(s.url).hostname}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function LlmAuditDetail() {
  const { projectId, campaignId, auditId } = useParams()
  const { data: audit, loading, error } = useApi<AuditData>(`/api/llm/audits/${auditId}`)
  const [activeTab, setActiveTab] = useState<LLMKey>('chatgpt')

  // ---- Analyse IA & livrable ----
  const [analysis, setAnalysis] = useState('')
  const [genLoading, setGenLoading] = useState(false)
  const [saveMsg, setSaveMsg] = useState('')

  useEffect(() => {
    if (audit?.analysis != null) setAnalysis(audit.analysis)
  }, [audit])

  const handleGenerate = async () => {
    setGenLoading(true); setSaveMsg('')
    const res = await apiPost<{ analysis: string | null }>(`/api/llm/audits/${auditId}/analysis/generate`, {})
    setGenLoading(false)
    if (res.data?.analysis) setAnalysis(res.data.analysis)
    else setSaveMsg("Génération IA indisponible — saisis l'analyse manuellement.")
  }

  const handleSaveAnalysis = async () => {
    const res = await apiPut(`/api/llm/audits/${auditId}/analysis`, { analysis })
    setSaveMsg(res.error ? `Erreur : ${res.error}` : 'Analyse enregistrée.')
  }

  const handleDownload = async () => {
    const res = await fetch(`/api/llm/audits/${auditId}/deliverable.pdf`, { credentials: 'include' })
    if (!res.ok) { setSaveMsg('Erreur lors de la génération du PDF.'); return }
    // Nom de fichier fourni par le serveur (Content-Disposition = nom de campagne), sinon fallback.
    const cd = res.headers.get('content-disposition') || ''
    let filename = `visibilite-ia-${auditId}.pdf`
    const star = cd.match(/filename\*=UTF-8''([^;]+)/i)
    const plain = cd.match(/filename="([^"]+)"/i)
    if (star) { try { filename = decodeURIComponent(star[1]) } catch { /* garde le fallback */ } }
    else if (plain) filename = plain[1]
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
  }

  // ---- Loading / error ----
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#6366F1] border-t-transparent" />
      </div>
    )
  }

  if (error || !audit || !audit.result) {
    return (
      <div className="rounded-xl border border-red-300 bg-red-50 p-6 text-center text-red-600">
        {error ?? 'Audit introuvable ou incomplet'}
      </div>
    )
  }

  const { result, campaign, costs: rawCosts } = audit
  const costs = rawCosts ?? []
  const { scores, competitors, raw_results, recommendations, tokens_total } = result
  const globalScore = scores.overall ?? scores.global ?? 0

  // Sorted competitors (competitors is Record<string, number>)
  const sortedCompetitors = Object.entries(competitors ?? {}).sort(
    ([, a], [, b]) => (b as number) - (a as number),
  )

  // Token totals
  const totalTokens = costs.reduce((a, c) => a + c.tokensIn + c.tokensOut, 0)

  // Breadcrumb helpers
  const campaignUrl = projectId
    ? `/p/${projectId}/ai/llm/${campaignId}`
    : `/ai/llm/${campaignId}`
  const listUrl = projectId ? `/p/${projectId}/ai/llm` : '/ai/llm'

  return (
    <div className="space-y-6">
      {/* ================================================================ */}
      {/* BREADCRUMB                                                       */}
      {/* ================================================================ */}
      <div className="flex items-center gap-2 text-sm text-[#555A73]">
        <Link to={listUrl} className="hover:text-[#1B2559] transition-colors">
          <ArrowLeft size={16} className="inline mr-1" />
          Visibilit&eacute; LLM
        </Link>
        <span>/</span>
        <Link to={campaignUrl} className="hover:text-[#1B2559] transition-colors">
          {campaign.name}
        </Link>
        <span>/</span>
        <span className="text-[#1B2559] font-medium">Audit du {formatDate(audit.createdAt)}</span>
      </div>

      {/* ================================================================ */}
      {/* ANALYSE IA & LIVRABLE                                            */}
      {/* ================================================================ */}
      <div className="bg-white rounded-xl border border-[#E2E4ED] p-5">
        <h3 className="font-semibold text-[#1B2559] mb-3">Analyse IA &amp; Livrable</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          <button onClick={handleGenerate} disabled={genLoading}
            className="px-3 py-2 text-sm rounded-lg bg-[#6366F1] text-white disabled:opacity-50">
            {genLoading ? 'Génération…' : "Générer l'analyse IA"}
          </button>
          <button onClick={handleSaveAnalysis}
            className="px-3 py-2 text-sm rounded-lg border border-[#E2E4ED] text-[#1B2559]">Enregistrer</button>
          <button onClick={handleDownload}
            className="px-3 py-2 text-sm rounded-lg bg-[#F59E0B] text-white">Télécharger le livrable</button>
        </div>
        <textarea value={analysis} onChange={e => setAnalysis(e.target.value)} rows={8}
          placeholder="L'analyse générée par l'IA apparaîtra ici. Tu peux la corriger avant de générer le livrable."
          className="w-full text-sm border border-[#E2E4ED] rounded-lg p-3" />
        {saveMsg && <p className="text-xs text-[#555A73] mt-2">{saveMsg}</p>}
      </div>

      {/* ---- Info banner ---- */}
      <div className="rounded-xl border border-[#E2E4ED] bg-[#F8F9FC] px-5 py-3 flex items-start gap-3">
        <Info size={16} className="text-[#6366F1] flex-shrink-0 mt-0.5" />
        <div className="text-xs text-[#555A73] leading-relaxed space-y-1">
          <p>Le <span className="font-medium text-[#1B2559]">score (0-100)</span> représente le taux de citation de votre marque dans les réponses. Cliquez sur une requête pour voir la réponse complète du LLM — votre marque est surlignée en <mark className="bg-emerald-100 text-emerald-800 px-0.5 rounded text-[10px]">vert</mark> et les concurrents en <mark className="bg-amber-100 text-amber-800 px-0.5 rounded text-[10px]">orange</mark>.</p>
        </div>
      </div>

      {/* ================================================================ */}
      {/* SUMMARY KPI ROW                                                  */}
      {/* ================================================================ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {(
          [
            ['Score global', globalScore, true],
            ['ChatGPT', scores.chatgpt, false],
            ['Gemini', scores.gemini, false],
            ['Claude', scores.claude, false],
          ] as [string, number, boolean][]
        ).map(([label, val, isMain]) => {
          // Compute citation rate for this LLM from raw_results
          let citationInfo = ''
          if (label !== 'Score global') {
            const key = label.toLowerCase() as LLMKey
            const responses = (raw_results ?? []).map((r) => r.responses?.[key]).filter(Boolean)
            const cited = responses.filter((r) => r?.cited).length
            if (responses.length > 0) {
              citationInfo = `${cited}/${responses.length} cité`
            }
          }

          return (
            <div
              key={label}
              className={`rounded-xl border border-[#E2E4ED] bg-white p-5 ${isMain ? 'ring-2 ring-[#6366F1]/20' : ''}`}
            >
              <div className="flex items-center gap-1.5 mb-2">
                {label !== 'Score global' && <LlmLogo llm={label} size={16} />}
                <span className="text-xs text-[#555A73] uppercase tracking-wider">{label}</span>
              </div>
              <div className="flex items-end gap-3">
                <span
                  className={`${isMain ? 'text-3xl' : 'text-2xl'} font-bold`}
                  style={{ color: scoreColor(val) }}
                >
                  {Math.round(val)}
                </span>
                <span className="text-sm text-[#8B8FA7] mb-0.5">/ 100</span>
              </div>
              {citationInfo && <p className="mt-2 text-xs text-[#8B8FA7]">{citationInfo}</p>}
            </div>
          )
        })}
      </div>

      {/* ================================================================ */}
      {/* BREAKDOWN BY LLM (TABS)                                          */}
      {/* ================================================================ */}
      <div className="rounded-xl border border-[#E2E4ED] bg-white">
        <div className="flex border-b border-[#E2E4ED]">
          {LLM_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-3 text-sm font-medium transition-colors relative ${
                activeTab === tab
                  ? 'text-[#6366F1]'
                  : 'text-[#555A73] hover:text-[#1B2559]'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <LlmLogo llm={tab} size={16} />
                {LLM_LABELS[tab]}
              </span>
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#6366F1]" />
              )}
            </button>
          ))}
        </div>

        <BreakdownTable
          results={raw_results}
          llm={activeTab}
          brand={campaign.brand}
          brandVariants={campaign.brandVariants ?? []}
          competitors={campaign.competitors ?? Object.keys(competitors ?? {})}
        />
      </div>

      {/* ================================================================ */}
      {/* COMPETITORS TABLE                                                */}
      {/* ================================================================ */}
      {sortedCompetitors.length > 0 && (
        <div className="rounded-xl border border-[#E2E4ED] bg-white">
          <div className="px-5 py-4 border-b border-[#E2E4ED]">
            <h2 className="text-base font-semibold text-[#1B2559]">Concurrents</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E4ED] text-left text-xs text-[#555A73] uppercase tracking-wider">
                  <th className="px-5 py-3">Concurrent</th>
                  <th className="px-5 py-3">Visibilit&eacute;</th>
                </tr>
              </thead>
              <tbody>
                {sortedCompetitors.map(([name, score]) => (
                  <tr
                    key={name}
                    className="border-b border-[#E2E4ED] last:border-0 hover:bg-[#F8F9FC] transition-colors"
                  >
                    <td className="px-5 py-3 font-medium text-[#1B2559]">{name}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-[#E2E4ED] rounded-full max-w-[200px]">
                          <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: scoreColor(score as number) }} />
                        </div>
                        <span className="text-xs font-bold tabular-nums" style={{ color: scoreColor(score as number) }}>
                          {score}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* RECOMMENDATIONS                                                  */}
      {/* ================================================================ */}
      {recommendations && recommendations.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-base font-semibold text-[#1B2559]">Recommandations</h2>
          {recommendations.map((rec, i) => (
            <div
              key={i}
              className={`rounded-xl border bg-white p-4 flex items-start gap-3 ${
                rec.type === 'warning'
                  ? 'border-l-4 border-l-[#F59E0B] border-[#E2E4ED]'
                  : 'border-l-4 border-l-[#3B82F6] border-[#E2E4ED]'
              }`}
            >
              {rec.type === 'warning' ? (
                <AlertTriangle size={18} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
              ) : (
                <Info size={18} className="text-[#3B82F6] flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className="text-sm font-medium text-[#1B2559]">{rec.title}</p>
                <p className="text-sm text-[#555A73] mt-0.5">{rec.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ================================================================ */}
      {/* COST BREAKDOWN                                                   */}
      {/* ================================================================ */}
      <div className="rounded-xl border border-[#E2E4ED] bg-white">
        <div className="px-5 py-4 border-b border-[#E2E4ED]">
          <h2 className="text-base font-semibold text-[#1B2559]">Consommation tokens</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E2E4ED] text-left text-xs text-[#555A73] uppercase tracking-wider">
                <th className="px-5 py-3">Modèle</th>
                <th className="px-5 py-3 text-right">Tokens entrée</th>
                <th className="px-5 py-3 text-right">Tokens sortie</th>
                <th className="px-5 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {costs.map((c, i) => (
                <tr
                  key={i}
                  className="border-b border-[#E2E4ED] last:border-0 hover:bg-[#F8F9FC] transition-colors"
                >
                  <td className="px-5 py-3 text-[#1B2559]">
                    <span className="inline-flex items-center gap-1.5">
                      <LlmLogo llm={c.provider} size={16} />
                      {c.model}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right text-[#555A73]">{fmtNum(c.tokensIn)}</td>
                  <td className="px-5 py-3 text-right text-[#555A73]">{fmtNum(c.tokensOut)}</td>
                  <td className="px-5 py-3 text-right text-[#1B2559] font-medium">
                    {fmtNum(c.tokensIn + c.tokensOut)}
                  </td>
                </tr>
              ))}
              {/* Total row */}
              <tr className="bg-[#F8F9FC] font-medium">
                <td className="px-5 py-3 text-[#1B2559]">
                  Total
                </td>
                <td className="px-5 py-3 text-right text-[#1B2559]">
                  {fmtNum(costs.reduce((a, c) => a + c.tokensIn, 0))}
                </td>
                <td className="px-5 py-3 text-right text-[#1B2559]">
                  {fmtNum(costs.reduce((a, c) => a + c.tokensOut, 0))}
                </td>
                <td className="px-5 py-3 text-right text-[#1B2559]">
                  {fmtNum(totalTokens)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
